import * as React from 'react';
import IPhotographer from './IPhotographer';
import { Translate } from '../../shared/LanguageService';
import {
    FormGroup,
    ControlLabel,
    InputGroup,
    Button,
    ButtonToolbar,
    FormControl,
    Table,
    Checkbox
} from 'react-bootstrap';
import FormSelect from '../../components/common/FormSelect';
import InlineTooltip from '../../components/common/ToolTip';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import DroneBookingsWarningModal from './DroneBookingsWarningModal';
import IUpcomingDroneBooking from './IUpcomingDroneBooking';
import PhotographersApi from './PhotographersApi';
import * as Site from '../../shared/Site';
import {getSiteName} from "../../shared/Site";

interface IPhotographerProductFormProps {
    user: any;
    onCheckboxChange: (e: any) => void;
    products: {
        items: {
            id: number;
            name: string;
            site: { id: number; name: string };
        }[];
        selected: number[];
    };
    productAdded: (id: number) => void;
    productRemoved: (id: number) => void;
    site: string;
}

export class PhotographerProductForm extends React.Component<IPhotographerProductFormProps, any> {
    constructor(props: any, context: any) {
        super(props, context);

        this.state = {
            selectedProduct: '',
            confirmUncheckCameraPole: false,
            upcomingDroneBookings: []
        };
        this.productSelectionChanged = this.productSelectionChanged.bind(this);
        this.disableAdd = this.disableAdd.bind(this);
        this.addProduct = this.addProduct.bind(this);
        this.onCameraPoleChange = this.onCameraPoleChange.bind(this);
        this.onDroneChange = this.onDroneChange.bind(this);
        this.uncheckDrone = this.uncheckDrone.bind(this);
        this.closeDroneWarning = this.closeDroneWarning.bind(this);
    }

    availableProducts() {
        return this.props.products.items.filter(
            (x: any) => this.props.products.selected.indexOf(x.id) < 0
        );
    }

    selectedProducts() {
        return this.props.products.items.filter(
            (x: any) => this.props.products.selected.indexOf(x.id) >= 0
        );
    }

    productSelectionChanged(e: any) {
        this.setState({ selectedProduct: e.target.value });
    }

    disableAdd() {
        return !(this.state.selectedProduct && this.state.selectedProduct.length > 0);
    }

    addProduct() {
        this.props.productAdded(parseInt(this.state.selectedProduct));
        this.setState({ selectedProduct: '' });
    }

    deleteProduct(id: number) {
        this.props.productRemoved(id);
        this.setState({ selectedProduct: `${id}` });
    }

    uncheckDrone() {
        this.props.onCheckboxChange({
            target: {
                name: 'hasDrone',
                checked: false
            }
        });
    }

    closeDroneWarning() {
        this.setState({ upcomingDroneBookings: [] });
    }

    onDroneChange(e) {
        const photographerId = this.props.user ? this.props.user.id : null;

        if (e.target.checked || !photographerId) {
            this.props.onCheckboxChange(e);
            return;
        }

        PhotographersApi.getUpcomingDroneBookings(photographerId)
            .then((bookings: IUpcomingDroneBooking[]) => {
                if (bookings && bookings.length > 0) {
                    this.setState({ upcomingDroneBookings: bookings });
                } else {
                    this.uncheckDrone();
                }
            })
            .catch(() => this.uncheckDrone());
    }

    onCameraPoleChange(e) {
        let anyAllocatedHeightProducts =
            this.selectedProducts().filter((x: any) => x.requiresPole).length > 0;
        if (!e.target.checked && anyAllocatedHeightProducts) {
            this.setState({ confirmUncheckCameraPole: true });
        } else {
            this.props.onCheckboxChange(e);
        }
    }

    render() {
        return (
            <fieldset>
                <legend>{Translate('photographers/modal/form/legend/product')}</legend>
                <Checkbox
                    name="hasCameraPole"
                    checked={this.props.user && this.props.user.hasCameraPole ? true : false}
                    onChange={this.onCameraPoleChange}
                >
                    <span>
                        {Translate('photographers/modal/form/checkbox-label/has-camera-pole')}
                    </span>
                </Checkbox>
                {this.state.confirmUncheckCameraPole && (
                    <ConfirmationModal
                        title={Translate(
                            'photographers/modal/form/has-camera-pole/confirmation/title'
                        )}
                        text={Translate(
                            'photographers/modal/form/has-camera-pole/confirmation/text'
                        )}
                        doButtonLabel={Translate(
                            'photographers/modal/form/has-camera-pole/confirmation/do-button'
                        )}
                        cancelButtonLabel={Translate(
                            'photographers/modal/form/has-camera-pole/confirmation/cancel-button'
                        )}
                        doButtonStyle="danger"
                        do={() => {
                            this.setState({ confirmUncheckCameraPole: false });
                            this.props.onCheckboxChange({
                                target: {
                                    name: 'hasCameraPole',
                                    checked: false
                                }
                            });
                            this.selectedProducts()
                                .filter((x: any) => x.requiresPole)
                                .map((x: any) => this.deleteProduct(x.id));
                        }}
                        closeModal={() => {
                            this.setState({ confirmUncheckCameraPole: false });
                        }}
                    />
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Checkbox
                        name="hasDrone"
                        checked={this.props.user && this.props.user.hasDrone ? true : false}
                        onChange={this.onDroneChange}
                    >
                        <span>
                            {Translate('photographers/modal/form/checkbox-label/has-drone')}
                        </span>
                    </Checkbox>
                    <InlineTooltip
                        id="has-drone-help"
                        tooltip={Translate(
                            'photographers/modal/form/checkbox-label/has-drone/help'
                        )}
                    />
                </div>
                {this.state.upcomingDroneBookings.length > 0 && (
                    <DroneBookingsWarningModal
                        bookings={this.state.upcomingDroneBookings}
                        linkToOrders={true}
                        onConfirm={this.uncheckDrone}
                        onClose={this.closeDroneWarning}
                    />
                )}
                <div className="row">
                    <FormGroup>
                        <div className="col-md-12">
                            <ControlLabel>
                                {Translate('photographer/modal/form/product/select-label/product')}
                            </ControlLabel>
                        </div>
                        <div className="col-md-10">
                            <FormSelect
                                onChange={this.productSelectionChanged}
                                value={this.state.selectedProduct ? this.state.selectedProduct : ''}
                                defaultTranslateKey="photographer/modal/form/product/select-label/default-value"
                            >
                                {this.availableProducts()
                                    .filter(
                                        (x: any) =>
                                            this.props.user.hasCameraPole ||
                                            (!this.props.user.hasCameraPole && !x.requiresPole)
                                    )
                                    .map(
                                        (product: {
                                            id: any;
                                            name: string;
                                            site: { id: number; name: string };
                                        }) => {
                                            return (
                                                <option value={product.id} key={product.id}>
                                                    {product.name} {this.props.site !== getSiteName(Site.Zentuvo) ? `(${product.site.name})` : null}
                                                </option>
                                            );
                                        }
                                    )}
                            </FormSelect>
                        </div>
                        <div className="col-md-2 pull-right">
                            <Button
                                bsSize="small"
                                bsStyle="success"
                                disabled={this.disableAdd()}
                                onClick={this.addProduct}
                            >
                                <i className="material-icons">add</i>{' '}
                                {Translate('photographer/modal/form/product/button-label/add')}
                            </Button>
                        </div>
                    </FormGroup>
                    <div className="col-md-12">
                        <Table hover responsive>
                            <thead>
                                <tr>
                                    <th>
                                        {Translate(
                                            'photographer/modal/form/product/table/product-name-header'
                                        )}
                                    </th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.selectedProducts().map(
                                    (product: {
                                        id: number;
                                        name: string;
                                        site: { id: number; name: string };
                                    }) => {
                                        return (
                                            <tr
                                                key={product.id}
                                                className={Site.getOrderCssClass(product.site.id)}
                                            >
                                                <td>{product.name}</td>
                                                <td>
                                                    <ButtonToolbar className="pull-right btn-group-sm">
                                                        <Button
                                                            bsStyle="danger"
                                                            onClick={() =>
                                                                this.deleteProduct(product.id)
                                                            }
                                                        >
                                                            <i className="material-icons">remove</i>{' '}
                                                            {Translate(
                                                                'photographer/modal/form/product/table/button-label/remove'
                                                            )}
                                                        </Button>
                                                    </ButtonToolbar>
                                                </td>
                                            </tr>
                                        );
                                    }
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </fieldset>
        );
    }
}

export default PhotographerProductForm;
